import { ArrowLeft, X } from '@/components/ui/icons';
import { useEffect, useRef, type ReactNode } from 'react';
import { useIsDesktop } from '@/hooks/useAppEffects';
import { cn } from '@/lib/utils';

export interface NavItem<T extends string> {
  id: T;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  onSelect?: () => void;
}

export interface NavGroup<T extends string> {
  title: string;
  items: Array<NavItem<T>>;
}

/**
 * Full-screen settings overlay with a sidebar nav. On phones the nav and the
 * content are two stacked screens.
 */
export function SettingsShell<T extends string>({
  groups,
  section,
  onSection,
  onClose,
  children,
  mobileShowContent,
  onMobileBack,
  footer,
}: {
  groups: Array<NavGroup<T>>;
  section: T;
  onSection: (s: T) => void;
  onClose: () => void;
  children: ReactNode;
  mobileShowContent: boolean;
  onMobileBack: () => void;
  /** Shown under the nav (e.g. links to the privacy policy). */
  footer?: ReactNode;
}) {
  const isDesktop = useIsDesktop();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog?.addEventListener('cancel', onCancel);
    return () => dialog?.removeEventListener('cancel', onCancel);
  }, [onClose]);

  const showNav = isDesktop || !mobileShowContent;
  const showContent = isDesktop || mobileShowContent;
  const current = groups.flatMap((g) => g.items).find((i) => i.id === section);

  return (
    <dialog ref={ref} aria-label="Settings" data-fullscreen className="m-0 h-dvh max-h-none w-screen max-w-none bg-surface p-0 text-fg">
      <div className="flex h-full">
        {showNav && (
          <nav className="flex w-full flex-col overflow-y-auto bg-sidebar md:w-[34%] md:max-w-[340px] md:min-w-[230px] md:items-end">
            <div className="w-full px-3 py-6 md:w-[230px] md:py-14">
              {!isDesktop && (
                <div className="mb-4 flex items-center justify-between px-2">
                  <h2 className="text-xl font-bold">Settings</h2>
                  <button type="button" onClick={onClose} aria-label="Close settings" className="rounded-full p-2 hover:bg-hover">
                    <X size={20} />
                  </button>
                </div>
              )}
              {groups.map((group) => (
                <div key={group.title} className="mb-5">
                  <h3 className="mb-1 px-2.5 text-[11px] font-bold tracking-wider text-fg-muted uppercase">{group.title}</h3>
                  <ul className="flex flex-col gap-0.5">
                    {group.items.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => (item.onSelect ? item.onSelect() : onSection(item.id))}
                          aria-current={section === item.id && !item.onSelect ? 'page' : undefined}
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-full px-3 py-2 text-left text-[15px] font-medium transition-colors duration-75',
                            item.danger ? 'text-danger hover:bg-danger-soft' : section === item.id && !item.onSelect ? 'bg-active text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg-soft',
                          )}
                        >
                          {item.icon}
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {footer && <div className="px-2.5 pt-2">{footer}</div>}
            </div>
          </nav>
        )}
        {showContent && (
          <div className="relative min-w-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[740px] px-4 py-6 md:px-10 md:py-14">
              {!isDesktop && (
                <button type="button" onClick={onMobileBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-fg-muted hover:text-fg">
                  <ArrowLeft size={16} /> Settings
                </button>
              )}
              <h2 className="mb-6 text-2xl font-extrabold tracking-tight">{current?.label}</h2>
              {children}
            </div>
            {isDesktop && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close settings (Esc)"
                className="fixed top-12 right-10 flex flex-col items-center gap-1 text-xs font-bold text-fg-muted hover:text-fg"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-current">
                  <X size={18} />
                </span>
                ESC
              </button>
            )}
          </div>
        )}
      </div>
    </dialog>
  );
}

export function SettingsCard({ title, description, children, className }: { title?: string; description?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('mb-6 rounded-2xl border border-line bg-sidebar p-5', className)}>
      {title && <h3 className="text-base font-bold">{title}</h3>}
      {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
      <div className={cn(title && 'mt-4')}>{children}</div>
    </section>
  );
}
